import { setLateFeeRule, useGetLateFeeRule } from '@myrentalmate/api-client';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { okData } from '../../lib/apiData';
import { formatMoney } from '../../lib/format';

const FIELDS: Field[] = [
  { name: 'enabled', label: 'Charge Late Fees at This Property', type: 'checkbox' },
  {
    name: 'grace_days',
    label: 'Grace Days',
    type: 'number',
    required: true,
    helperText: 'Days after the due date before a fee applies',
  },
  {
    name: 'fee_type',
    label: 'Fee Type',
    type: 'select',
    required: true,
    options: [
      { value: 'flat', label: 'Flat amount' },
      { value: 'percent', label: 'Percent of rent' },
    ],
  },
  { name: 'amount', label: 'Amount or Percent', type: 'number', required: true },
  {
    name: 'max_amount',
    label: 'Most It Can Be',
    type: 'number',
    helperText: 'Optional cap. Check the limit your state allows',
  },
];

export function LateFeeRuleSection({
  propertyId,
  canManage,
}: {
  propertyId: number;
  canManage: boolean;
}) {
  const rule = okData(useGetLateFeeRule(propertyId).data);
  const [editing, setEditing] = useState(false);
  if (!rule) return null;
  const fee =
    rule.fee_type === 'percent' ? `${Number(rule.amount)}% of rent` : formatMoney(rule.amount);
  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Late Fees
        </Typography>
        {canManage && (
          <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
            Change
          </Button>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography>
          {rule.enabled
            ? `${fee}, ${rule.grace_days} days after the due date${
                rule.max_amount === null ? '' : `, up to ${formatMoney(rule.max_amount)}`
              }`
            : 'Off. No late fees are charged at this property.'}
        </Typography>
      </Paper>
      {editing && (
        <FormDialog
          title="Late Fees"
          fields={FIELDS}
          initial={{
            enabled: rule.enabled,
            grace_days: String(rule.grace_days),
            fee_type: rule.fee_type,
            amount: Number(rule.amount) ? Number(rule.amount).toFixed(2) : '',
            max_amount: rule.max_amount === null ? '' : Number(rule.max_amount).toFixed(2),
          }}
          onSubmit={(values) =>
            setLateFeeRule(propertyId, {
              enabled: Boolean(values.enabled),
              grace_days: Number(values.grace_days),
              fee_type: required(values, 'fee_type') as 'flat' | 'percent',
              amount: required(values, 'amount'),
              max_amount: optional(values, 'max_amount'),
            })
          }
          onClose={() => setEditing(false)}
        />
      )}
    </Stack>
  );
}
